
/**
 * @file src/components/BallCard.js
 */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Tooltip from 'rc-tooltip';
import ReactTooltip from 'react-tooltip';

// import noImageAvailable from '../../../styles/images/no-image-available.png';
import _ from 'lodash';

/**
  * @class BallCard
  * @description Baseball card popup for mention/entity information
  * @property {object} info - The tooltip info associated with the segment to highlight
  * @property {number} delay - Delay (ms) that occurs before card is opened.
  * @property {string} trigger - The trigger for the opening of the card (hover, click, focus)
  * @property {func} onClose - Gets fired when the card is closed.
  * @property {func} onOpen - Gets fired when the card is opened.
  */
class BallCard extends Component {
 
    /**
      * @constructor
      * @param {*} props - Class properties 
      */
    constructor(props) {
        super(props);
         
        this.renderNodeTooltip = this.renderNodeTooltip.bind(this);
        this.renderEdgeTooltip = this.renderEdgeTooltip.bind(this);
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.nodeImageError = this.nodeImageError.bind(this);
        this.state = {
            show: false,
            type: '',
            info: this.props.info,
            offset: null,
        }
 
        this.id = 'card-id-' + Math.random().toString(36).substr(2, 16);
    }

    /**
     * @function componentWillReceiveProps 
     * @description Called when component props change
     * @param {*} nextProps 
     */
    componentWillReceiveProps(nextProps) {
        if (!_.isEqual(nextProps, this.props)) {
            this.setState({
                info: nextProps.info,
                type: nextProps.type
            })
        }
    }

    open(type, info, offset) {
        this.setState({
            show: true,
            type,
            info,
            offset
        }, () => {
            if (this.props.onOpen) {
                this.props.onOpen();
            }
        })
    }

    close() {
        this.setState({
            show: false,
            type:'',
            info: null,
        }, () => {
            if (this.props.onClose) {
                this.props.onClose();
            }
        })
    }

    nodeImageError({currentTarget}){ 
        if (this.state.info && this.state.info.imageUrls && this.state.info.imageUrls.length > 0) {
            const info = this.state.info; 
            const set = new Set(info.imageUrls);
            const newArray = Array.from(set);
            if(newArray.indexOf(currentTarget.src) < newArray.length-1){
                currentTarget.src=newArray[newArray.indexOf(currentTarget.src)+1]
            }
            else{
                ReactTooltip.hide();
                this.setState({info:Object.assign({}, info,{imageUrls:[]})})
            }
        }
        else{
            ReactTooltip.hide();
            this.setState({info:Object.assign({}, info,{imageUrls:[]})  })
        }
    }
    // e.currentTarget.src = 'images/no-image-available.png';
    
    /**
      * Render contents of the baseball card inside the popover control.
      * @returns React component
      */
    renderNodeTooltip() {
        const info = this.state.info;
        if (info) {
            // Handle language specific label and description fields
            var label = info.label;
            var description = info.dsgFeatureCodeLabel || info.description || null;
            if ('languageFields' in info) {
                info.languageFields.forEach(function (arrayItem) {
                    if ('langCode' in arrayItem && arrayItem.langCode === 'en') {
                        if ('label' in arrayItem) {
                            label = arrayItem.label;
                        }
                        if ('description' in arrayItem) {
                            description = arrayItem.description;
                        }
                    }
                });
            }
            return (
                <div className="bbc" style={{ color:'white',background:'black', padding:'15px', opacity:.9, borderRadius:'15px' }} >
                    <div className="bbc-header">
                        <div className="bbc-label">
                            {label} 
                        </div>           
                    </div>
                    <div className="bbc-description">
                        {this.state.info.imageUrls && this.state.info.imageUrls.length>0 &&
                            <img src={this.state.info.imageUrls[0]} onError={this.nodeImageError}/>
                        }
                        <div>{description}</div>
                    </div>
                </div>
            );
        }
    }

    
    /**
     * Render the tooltip contents for the edge/relationship that is hovered over.
     * @returns React component
     */
    renderEdgeTooltip() {
        const info = this.state.info
        if (info) {
            let evidence = null;

            let type = info.predicate.type || '';
            if ('properties' in info.predicate && type in info.predicate.properties) {
                type = info.predicate.properties[type];
            }

            if (info.evidence && info.evidence.sentence) {
                evidence = info.evidence.sentence;
            }

            return(
                // <div className="bbc">
                <div className="bbc" style={{ color:'white',background:'black', padding:'15px', opacity:.9, borderRadius:'15px' }} >
                    <div className="bbc-header">
                        <div className="bbc-label">
                            {evidence ? 
                                <span className="bbc-org-instance">Evidence ({type})</span>
                            :
                                <span className="bbc-org-instance"> 
                                    {info.subject.label || info.subject.anchor} 
                                    <span className={`fa ${info.bi ? 'fa-arrows-h' : 'fa-long-arrow-right'}`} style={{marginLeft: '3px', marginRight: '3px'}}></span>
                                    {type} 
                                    <span className={`fa ${info.bi ? 'fa-arrows-h' : 'fa-long-arrow-right'}`} style={{marginLeft: '3px', marginRight: '3px'}}></span>
                                    {info.object.label || info.object.anchor}
                                </span>
                            }
                        </div>           
                    </div>
                    <div className="bbc-description">
                        {evidence ? 
                            <div>{evidence}</div>
                        :
                            <div>Confidence: {info.confidence || 'N/A'}</div>
                        }
                    </div>
                    {'properties' in info.predicate &&
                    <div className="bbc-meta" style={{background:'transparent'}}>
                        <div className="bbc-meta-entry-wrapper">
                            {Object.keys(info.predicate.properties).map((prop, indx) => {
                                return <span key={indx} className="bbc-meta-entry">
                                    {prop}: {info.predicate.properties[prop]}
                                </span>
                            })}
                        </div>
                    </div>
                    }
                </div>
            )
        }
    }

    /**
      * Render the component
      * @returns React component
      */
    render() {

        const align = this.state.offset ? this.state.offset : [0,0]

        return (
            <Tooltip 
                // visible={this.state.show && this.state.info}
                visible={true}
                trigger={this.props.trigger}
                onVisibleChange={(visible) => {
                    this.setState({ show: visible }, () => {
                        if (!this.state.show && this.props.onClose) {
                            this.props.onClose();
                        } else if (this.state.show && this.props.onOpen) {
                            this.props.onOpen();
                        }
                    });
                }}
                placement="right"
                align={align}
                overlayClassName="bb-card" 
                id={this.id} 
                mouseEnterDelay={1.5} 
                overlayStyle={{position:'absolute', zIndex:1, left: align[0], top:align[1]}}
                overlay={() => {
                    if (this.state.type === 'node' || this.state.type === 'edge') { 
                        return this.state.type === 'node' ? this.renderNodeTooltip() : this.renderEdgeTooltip()
                    }
                    return null;
                }}>
                {this.props.children && React.cloneElement(this.props.children)}
            </Tooltip>
        ) 
    }
};
 
BallCard.defaultProps = {
    info: null,
    trigger: 'hover',
    onOpen: null,
    onClose: null
};

BallCard.propTypes = {
    info: PropTypes.object,
    trigger: PropTypes.string,
    onClose: PropTypes.func,
    onOpen: PropTypes.func
};
 
export default BallCard;